<template>
  <modal title="" :close="close" title-border large>
    <template #type>
      <div class="flex w-full items-center justify-between">
        <div class="flex items-center space-x-4">
          <span class="text-lg font-semibold text-gray-600">
            {{ scorecard.name }}
          </span>
          <scorecard-id-badge :model-value="scorecard.id" />

          <lf-badge>
            {{ $t("SCORECARDS.MANUAL") }}
          </lf-badge>
        </div>
        <div class="flex items-center space-x-2">
          <scorecard-favorite-button :model="scorecard" />
          <run-scorecard-button
            v-if="canRunScoreCard"
            :model="scorecard"
            class="mt-2"
            @scorecard:run="handleRun"
          />
        </div>
      </div>
    </template>
    <template #content>
      <div class="flex w-full flex-col space-y-4 p-5">
        <p>{{ scorecard.description }}</p>

        <div
          class="flex items-center justify-between rounded-sm border border-gray-200 bg-gray-50 px-5 py-3"
        >
          <span class="font-semibold text-gray-600">
            {{ $t("SCORECARDS.TOTAL_PTS") }}
          </span>
          <lf-input
            class="-mb-0 h-9 max-w-30"
            name="total_points"
            type="number"
            :value="points"
            @key-released="handlePtsChange"
          >
            <template #suffix>
              <span class="text-sm text-gray-300">
                {{ $t("SCORECARDS.POINTS_PTS") }}
              </span>
            </template>
          </lf-input>
        </div>
        <div
          class="mt-5 flex justify-end space-x-4 border-t border-gray-200 pt-5"
        >
          <outline-button @click="close">
            {{ $t("COMMON.CANCEL") }}
          </outline-button>
          <primary-button @click="handleSave">
            {{ $t("COMMON.SAVE") }}
          </primary-button>
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { Ability, PermissionSubject } from "@/enums/auth";
import { usePermissions } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import { useDealsBase } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import { useActiveStage } from "@/hooks/workflow";

import RunScorecardButton from "@/components/scorecards/RunScorecardButton.vue";
import ScorecardFavoriteButton from "@/components/scorecards/ScorecardFavoriteButton.vue";
import ScorecardIdBadge from "@/components/scorecards/ScorecardIdBadge.vue";
import LfBadge from "@/components/ui/LfBadge.vue";
import Modal from "@/components/ui/Modal.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";

import type {
  IRunScorecardGroupsPayload,
  IRunScorecardsPayload,
  IScorecard,
} from "@/models/scorecards";
import type { PropType } from "vue";

const props = defineProps({
  close: {
    type: Function as PropType<() => void>,
    required: true,
  },
  scorecard: {
    type: Object as PropType<IScorecard>,
    required: true,
  },
});

const { dispatch, commit } = useStore();
const { activeDeal } = useDealsBase();
const { t } = useI18n();
const { showMessage } = useNotification();
const { canPerformActionReactive } = usePermissions();
const { activeStage } = useActiveStage();

const canRunScoreCard = canPerformActionReactive(
  PermissionSubject.scoreCard,
  Ability.run,
  {
    scoreCard: props.scorecard?.id,
    scoreCardStageId: activeStage.value?.id,
    application: activeDeal.value.id,
  }
);

const getPoints = () => {
  if (!props.scorecard.results?.length) {
    return 0;
  }
  return props.scorecard.results[0].points || 0;
};

const points = ref(getPoints());

const handlePtsChange = (val: string) => {
  points.value = Number(val);
};

const handleSave = async () => {
  if (!activeDeal.value?.id) {
    return;
  }

  if (isNaN(points.value)) {
    showMessage(t("SCORECARDS.INVALID_VALUE"), "error");
    return;
  }

  const maxValue = Math.max(points.value, props.scorecard.points || 0);

  if (
    Number(maxValue) === Number(points.value) &&
    Number(points.value) !== Number(props.scorecard.points)
  ) {
    showMessage(
      t("SCORECARDS.VALUE_BIGGER_THAN_TOTAL_ERROR", {
        total: props.scorecard.points || 0,
      }),
      "error"
    );
    return;
  }

  try {
    await dispatch("scorecards/updatePoints", {
      appId: activeDeal.value.id,
      scorecardId: props.scorecard.id,
      points: parseFloat(String(points.value)),
    });
    props.close();
  } catch (error) {
    commit("setGlobalMessage", {
      title: t("COMMON.ERROR_OCCURRED"),
      type: "error",
    });
  }
};

const { fetchWrapper: handleRun } = usePromiseWrapper(async () => {
  if (!activeStage.value) {
    return;
  }

  const payload: {
    appId: string;
    data: IRunScorecardsPayload | IRunScorecardGroupsPayload;
  } = {
    appId: activeDeal.value.id,
    data: { stage_id: activeStage.value.id },
  };

  payload.data = {
    ["score_card_ids"]: [props.scorecard.id],
    stage_id: activeStage.value.id,
  };

  await dispatch("scorecards/runScorecards", payload);
});
</script>
